// src/lib/dashboard/geracaoMensal.ts
import type { JanelaPeriodo } from './periodoDashboard.ts';

const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
export const MAX_MESES_GERACAO = 24;

export interface MesReferenciaData {
  ano: number;
  mes: number; // 0-11
  label: string; // "JAN/2026"
}

export interface LinhaEnergiaAcumulada {
  site_id_api: string;
  data: string; // "YYYY-MM-DD"
  energia_total_kwh: number | null;
}

export interface PontoGeracaoMensal {
  mesReferencia: string;
  energiaGeradaKwh: number;
}

export interface PontoEsperadoMensal {
  mesReferencia: string;
  energiaEsperadaKwh: number;
}

function formatarDataISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Lista os meses (ano/mês) cobertos pela janela, do mais antigo pro mais
// recente, limitado aos últimos `maxMeses` — evita gráfico ilegível e um
// array de datas grande demais pra RPC quando o filtro é "histórico" (janela
// null) ou cobre um intervalo muito longo.
export function mesesDoIntervalo(janela: JanelaPeriodo | null, hoje: Date, maxMeses: number = MAX_MESES_GERACAO): MesReferenciaData[] {
  const fimAno = janela ? janela.fim.getFullYear() : hoje.getFullYear();
  const fimMes = janela ? janela.fim.getMonth() : hoje.getMonth();

  let qtdeMeses: number;
  if (janela) {
    const inicioAno = janela.inicio.getFullYear();
    const inicioMes = janela.inicio.getMonth();
    const totalMeses = (fimAno - inicioAno) * 12 + (fimMes - inicioMes) + 1;
    qtdeMeses = Math.min(Math.max(totalMeses, 1), maxMeses);
  } else {
    // Sem janela (histórico): sempre os últimos `maxMeses` terminando hoje —
    // não há intervalo real pra clampar contra.
    qtdeMeses = maxMeses;
  }

  const lista: MesReferenciaData[] = [];
  for (let i = qtdeMeses - 1; i >= 0; i--) {
    const d = new Date(fimAno, fimMes - i, 1);
    lista.push({ ano: d.getFullYear(), mes: d.getMonth(), label: `${MESES[d.getMonth()]}/${d.getFullYear()}` });
  }
  return lista;
}

// N+1 datas pra RPC: o último dia do mês ANTERIOR ao primeiro mês da lista
// (baseline, pra calcular a geração do primeiro mês por diferença) + o
// último dia de cada mês da lista.
export function datasCorteParaRpc(meses: MesReferenciaData[]): string[] {
  if (meses.length === 0) return [];
  const primeiro = meses[0];
  const baseline = new Date(primeiro.ano, primeiro.mes, 0); // dia 0 = último dia do mês anterior
  const datasBase = meses.map((m) => new Date(m.ano, m.mes + 1, 0)); // último dia de cada mês
  return [baseline, ...datasBase].map(formatarDataISO);
}

// Soma `energia_total_kwh` entre todas as usinas por data de corte, e calcula
// a geração de cada mês como (acumulado no fim do mês) - (acumulado no fim do
// mês anterior). Usina sem leitura numa data de corte não entra na soma
// daquela data (equivale a considerá-la 0 até sua primeira leitura real).
// Nunca devolve negativo — uma leitura que "caiu" (erro do coletor) vira 0,
// não um número negativo sem sentido físico.
export function calcularGeracaoMensal(meses: MesReferenciaData[], linhas: LinhaEnergiaAcumulada[]): PontoGeracaoMensal[] {
  const datas = datasCorteParaRpc(meses);
  const totalPorData = new Map<string, number>();
  linhas.forEach((l) => {
    if (l.energia_total_kwh == null) return;
    totalPorData.set(l.data, (totalPorData.get(l.data) || 0) + Number(l.energia_total_kwh));
  });
  return meses.map((m, i) => {
    const inicio = totalPorData.get(datas[i]) ?? 0;
    const fim = totalPorData.get(datas[i + 1]) ?? 0;
    return { mesReferencia: m.label, energiaGeradaKwh: Math.max(0, fim - inicio) };
  });
}

// Energia esperada = capacidade instalada (MWp → kWp) × fator (kWh/kWp/dia)
// × dias do mês.
export function calcularEsperadoMensal(meses: MesReferenciaData[], capacidadeTotalMwp: number, fatorKwhPorKwpDia: number): PontoEsperadoMensal[] {
  const capacidadeKwp = capacidadeTotalMwp * 1000;
  return meses.map((m) => {
    const diasNoMes = new Date(m.ano, m.mes + 1, 0).getDate();
    return { mesReferencia: m.label, energiaEsperadaKwh: capacidadeKwp * fatorKwhPorKwpDia * diasNoMes };
  });
}
